import { BarChart3, Mail } from "lucide-react";
import { heardFromOptions } from "@/components/site/contact/useContactForm";
import { slugify } from "./adminUtils";

const plural = (n) => `enquir${n === 1 ? "y" : "ies"}`;

function SourceChip({ source, count, active, onClick }) {
  const zero = count === 0 && !active;
  let cls = "bg-[#8B5CF6]/10 text-[#6D28D9] hover:bg-[#8B5CF6]/20";
  if (active) cls = "bg-[#6D28D9] text-white shadow-md";
  else if (zero) cls = "bg-slate-50 text-[#94A3B8] border border-dashed border-slate-200 hover:bg-slate-100";
  let badgeCls = "bg-[#8B5CF6] text-white";
  if (active) badgeCls = "bg-white text-[#6D28D9]";
  else if (zero) badgeCls = "bg-slate-200 text-[#64748B]";
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full font-semibold text-sm px-4 py-2 transition-all hover:-translate-y-0.5 ${cls}`}
      aria-pressed={active}
      data-testid={`attribution-${slugify(source)}`}
    >
      {source}
      <span className={`grid place-items-center min-w-6 h-6 px-1.5 rounded-full text-xs ${badgeCls}`}>{count}</span>
    </button>
  );
}

function SourcePeople({ source, people, onClear }) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="source-filter-bar">
      <p className="text-sm text-[#334155]">
        Showing <strong>{people.length}</strong> {plural(people.length)} via <strong>{source}</strong>
      </p>
      <button onClick={onClear} className="text-sm font-semibold text-[#FF7A1A] hover:underline" data-testid="clear-source-filter">
        Show all
      </button>
      <a
        href={`mailto:?bcc=${people.map((e) => e.email).join(",")}`}
        className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A2540] bg-white border border-slate-200 hover:bg-slate-50 px-4 py-2 rounded-full transition-colors"
        data-testid="email-source-group"
      >
        <Mail size={14} /> Email all {people.length} (BCC)
      </a>
      <ul className="w-full mt-1 divide-y divide-slate-100 rounded-xl border border-slate-100 bg-[#F8FAFC] max-h-64 overflow-y-auto" data-testid="source-people-list">
        {people.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
            <span className="font-semibold text-[#0F172A]">{e.name}</span>
            <a href={`mailto:${e.email}`} className="text-[#334155] hover:text-[#FF7A1A]">{e.email}</a>
            <span className="text-xs text-slate-400 w-full sm:w-auto">{new Date(e.created_at).toLocaleDateString("en-GB")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AttributionPanel({ enquiries, sourceFilter, onToggleSource, onClear, filteredList }) {
  const counts = enquiries.reduce((acc, e) => {
    if (e.heard_from) acc[e.heard_from] = (acc[e.heard_from] || 0) + 1;
    return acc;
  }, {});
  // Every form option (including 0 counts), plus any legacy values that no longer appear in the form.
  const sources = [...heardFromOptions, ...Object.keys(counts).filter((s) => !heardFromOptions.includes(s))];
  const attribution = sources.map((s) => [s, counts[s] || 0]).sort((a, b) => b[1] - a[1]);
  const unanswered = enquiries.length - Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="mb-8 bg-white rounded-[1.5rem] p-6 border border-slate-100 shadow-sm" data-testid="admin-attribution">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <BarChart3 size={18} className="text-[#8B5CF6]" />
        <h2 className="font-heading text-lg font-semibold text-[#0F172A]">Where enquiries come from</h2>
        <span className="text-xs text-[#94A3B8]">— click a source to see who</span>
        {unanswered > 0 && (
          <span className="ml-auto text-xs text-[#94A3B8]" data-testid="attribution-unanswered">{unanswered} didn't say</span>
        )}
      </div>
      <div className="flex flex-wrap gap-2.5">
        {attribution.map(([source, count]) => (
          <SourceChip key={source} source={source} count={count} active={sourceFilter === source} onClick={() => onToggleSource(source)} />
        ))}
      </div>
      {sourceFilter && <SourcePeople source={sourceFilter} people={filteredList} onClear={onClear} />}
    </div>
  );
}
